import { Form } from 'formik'
import styled, { css } from 'styled-components'

import { plusSVGDataURL } from '@src/components/SVGS/PlusSVG'
import { tickInCircleSVGDataURL } from '@src/components/SVGS/TickInCircleSVG'
import { breakpoints } from '@src/constants/breakpoints'

const FORM_PADDING = {
  mobile: 120,
  tablet: 180,
} as const

export const FlexGrowForm = styled(Form)<{
  $windowHeight: number
  $hasSafeArea: boolean
}>`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 100%;
  overflow: hidden;
  box-sizing: border-box;
  height: ${props => props.$windowHeight - FORM_PADDING.mobile}px;

  @media (min-width: ${breakpoints.largeTablet}px) {
    height: ${({ $hasSafeArea, $windowHeight }) =>
      $hasSafeArea ? `${$windowHeight - FORM_PADDING.tablet}px` : `100vh`};
  }
`

export const FlexGrowScrollContainer = styled.div`
  flex-grow: 1;
  overflow-y: auto;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
  touch-action: pan-y;
  overscroll-behavior-y: contain;

  @media (max-width: ${breakpoints.largeTablet}px) {
    padding-bottom: ${FORM_PADDING.tablet}px;
  }

  @media (max-width: ${breakpoints.tablet}px) {
    padding-bottom: ${FORM_PADDING.mobile}px;
  }
`

export const CheckoutStepForm = styled(Form)`
  height: 100%;
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 0 32px 32px;
  box-sizing: border-box;
`

export const ScrollContainer = styled.div`
  overflow-y: scroll;
  flex-grow: 1;
`

export const TitleAndErrorContainer = styled.div<{ addressError: boolean }>`

${({ addressError }) =>
  addressError &&
  css`
    animation: shake-effect 0.8s, highlight-red 3s;
  `}


  @keyframes highlight-red {
    0% {
      color: red;
    }
  }

  @keyframes shake-effect {
    0% {
      transform: translateX(0);
    }
    10% {
      transform: translateX(4px);
    }
    30% {
      transform: translateX(-4px);
    }
    50% {
      transform: translateX(4px);
    }
    70% {
      transform: translateX(-4px);
    }
    90% {
      transform: translateX(4px);
    }
    100% {
      transform: translateX(0px);
    }

`

export const ErrorMessage = styled.p`
  color: #e1292f;
  border-radius: 4px;
  border: 2px solid #e1292f;
  padding: 16px;
  font-size: 14px;
  font-weight: 400;
`

export const StepTitle = styled.h3`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 24px 0 16px;
  font-size: 20px;
  font-weight: 700;
`

export const FulfilmentSectionContainer = styled.fieldset`
  display: flex;
  flex-direction: column;
  border: none;
  margin: 0 0 16px;
  padding: 0;
`

export const RadioButton = styled.input.attrs({
  type: 'radio',
})<{
  $uncheckedSVGName?: string
  $checkedSVGName?: string
}>`
  /* Hide the default radio button */
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;

  /* Custom radio button style: black circle */
  width: 24px;
  height: 24px;
  background-color: #fff;
  border: 2px solid #ebecf0;
  border-radius: 50%;
  position: relative;
  margin: 0;
  box-sizing: border-box;
  cursor: pointer;

  &:checked {
    background-color: #000;
    border: 2px solid #000;
  }

  &:not(:checked)::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 16px;
    height: 16px;
    margin: 2px;
    background-image: ${props => props.$uncheckedSVGName || 'non'};
    background-repeat: no-repeat;
    background-position: center;
  }

  &:checked::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 16px;
    height: 16px;
    margin: 2px;
    background-image: ${props =>
      props.$checkedSVGName || tickInCircleSVGDataURL};
    background-repeat: no-repeat;
    background-position: center;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }

  &:focus {
    /* Custom style for radio button focus state */
    outline: none;
  }

  &:hover {
    /* Custom style for radio button focus state */
    outline: none;
    border: 2px solid #000;
  }
`

export const AddressAndFriendlyNameContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
`

export const AddressContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 10px;
`

const BaseText = css`
  margin: 0;
  line-height: 20px;
`

export const Label = styled.label`
  font-weight: 700;
  cursor: pointer;
  padding-left: 8px;
`

export const Text = styled.p`
  ${BaseText}
  font-weight: 400;
  font-size: 15px;
  padding-left: 8px;
`

export const UnavailableText = styled.p`
  ${BaseText}
  font-weight: 400;
  font-size: 12px;
`

export const SelectedLabel = styled(Label)`
  font-weight: 500;
  cursor: pointer;
  margin-bottom: 3px;
`

export const NoteLabel = styled.label`
  font-weight: 700;
  cursor: pointer;
`

// A radio button which renders a plus icon when unchecked and the default tick icon when checked
export const PlusUncheckedRadioButton = styled(RadioButton).attrs({
  $uncheckedSVGName: plusSVGDataURL,
})``

export const SpaceBetweenContent = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  padding: 0 32px;
`

export const FulfilmentNoteContainer = styled.div``

export const ContentWrapper = styled.div`
  padding: 8px 16px;

  @media (min-width: ${breakpoints.tabletMisc}px) {
    padding: 16px 32px;
  }

  @media (min-width: ${breakpoints.tabletMisc}px) and (pointer: coarse) {
    padding: 0 32px 200px 32px;
  }
`
